import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../apiservice/apiservice.service';
import { ConfigService } from '../config/config.service';
import { Library, LibraryInfo } from '../../core/interfaces/library.interface';
import { ApiResponse } from '../api-response.interface';
import { HttpParams } from '@angular/common/http';
import { MaterialType } from '../holdings/holdings.service';
import { HttpClient } from '@angular/common/http';
import { Format } from '../../core/interfaces/format.interface';
import { FormControl, ValidationErrors } from '@angular/forms';
import { SharedService } from '../shared/shared.service';

export interface LibraryType {
  id: number;
  name: string;
}

export interface UserLocation extends Location {
  isDefault: boolean;
}

/**
 * Represents a location within a Library,
 * however it can also represent a physical library (Branch)
 * as with HCOLS and isMultiBranch=true
 */
export interface Location {
  id: number;
  name: string;
  /**
   * For loc1, displayName will concat 'loc2 - loc1', otherwise Name will be used.
   */
  readonly displayName: string;
  /**
   * If loc3 (parentId = 0), loc.name, otherwise loc.name + displayName
   */
  readonly fullName: string;
  level: number;
  library: string;
  libraryProfileKey: number;
  parentId: number;
  /**
   * Whether the location is active and visible in selection lists
   */
  active: boolean;
  /**
   * Hide Location from search result locations (OPAC)
   */
  hide: boolean;
  /**
   * Suppress search results that are associated with this location (OPAC)
   */
  suppress: boolean;
}

/** Location Hierarchy.; Loc3, loc3, Loc1 */
export interface LocationNode extends Location {
  locations: LocationNode[];
}

/** Custom Location Groups that span loc3, loc2 */
export interface CirculationGroup {
  id: number;
  name: string;
  locations?: Location[];
}

@Injectable({
  providedIn: 'root',
})
export class LibraryService {
  private _http = inject(HttpClient);
  private _apiService = inject(ApiService);
  private _configService = inject(ConfigService);
  private _sharedService = inject(SharedService);

  private readonly _libraryApi = `${this._configService.agApi}/library`;
  private readonly _userData = this._sharedService.userData;
  private _libraries = new BehaviorSubject<Library[]>([]);
  libraries$ = this._libraries.asObservable();

  getLibraries(): Observable<Library[]> {
    return this._apiService.get(this._libraryApi).pipe(
      map((response) => {
        if (response) {
          this._libraries.next(response.data);
          return response;
        } else {
          throw new Error('Error retrieving libraries');
        }
      }),
    );
  }

  getLibraryTypes(): Observable<LibraryType[]> {
    return this._apiService.get(`${this._libraryApi}/types`).pipe(
      map((response) => {
        if (response) {
          return response;
        } else {
          throw new Error('Error retrieving Library Types');
        }
      }),
    );
  }

  getLibraryType(id: number): Observable<LibraryType> {
    return this._apiService.get(`${this._libraryApi}/types/${id}`).pipe(
      map((response) => {
        if (response) {
          return response;
        } else {
          throw new Error(`Error retrieving Library Type by Id ${id}`);
        }
      }),
    );
  }

  getGroups(lpk: number): Observable<CirculationGroup[]> {
    const url = `${this._libraryApi}/${lpk}/circulationgroups`;
    return this._apiService.get(url).pipe(
      map((response: ApiResponse<CirculationGroup[]>) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  createGroup(lpk: number, payload: CirculationGroup): Observable<any> {
    const url = `${this._libraryApi}/${lpk}/circulationgroups`;
    return this._apiService.post(url, payload);
  }

  updateGroup(lpk: number, payload: CirculationGroup): Observable<any> {
    const url = `${this._libraryApi}/${lpk}/circulationgroups/${payload.id}`;
    return this._apiService.put(url, payload);
  }

  deleteGroup(lpk: number, id: number): Observable<any> {
    const url = `${this._libraryApi}/${lpk}/circulationgroups/${id}`;
    return this._apiService.delete(url);
  }

  getLocations(lpk: number, level: number = 1): Observable<Location[]> {
    const url = `${this._libraryApi}/${lpk}/locations`;
    return this._apiService
      .get(url, {
        params: {
          levelFilter: level,
        },
      })
      .pipe(
        map((response: ApiResponse<Location[]>) => {
          if (response.data) {
            return response.data;
          } else {
            throw new Error(response.message);
          }
        }),
      );
  }

  getLocationsByLpks(lpks: number[], level: number = 1): Observable<Location[]> {
    const url = `${this._libraryApi}/locations`;
    let params = new HttpParams();
    for (const lpk of lpks) {
      params = params.append('lpks', lpk.toString());
    }
    params = params = params.append('levelFilter', level.toString());
    return this._apiService
      .get(url, {
        params,
      })
      .pipe(
        map((response: ApiResponse<Location[]>) => {
          if (response.data) {
            return response.data;
          } else {
            throw new Error(response.message);
          }
        }),
      );
  }

  searchLibraries(term: string): Observable<LibraryInfo[]> {
    return this._http
      .get<LibraryInfo[]>(this._libraryApi + '/search', {
        params: {
          query: term,
        },
      })
      .pipe(
        map((response) => {
          if (response) {
            return response;
          } else {
            throw new Error('Error retrieving libraries');
          }
        }),
      );
  }

  getHomeLocations(lpk: number, staff = false): Observable<Location[]> {
    const url = `${this._libraryApi}/${lpk}/homelocations`;
    return this._apiService.get(url, { params: { staff } });
  }

  getMaterialTypes(lpk: number): Observable<MaterialType[]> {
    const url = `${this._libraryApi}/${lpk}/materialtypes`;
    return this._apiService.get(url);
  }

  getMaterialTypesByLpks(lpks: number[]): Observable<MaterialType[]> {
    const url = `${this._libraryApi}/materialtypes`;
    let params = new HttpParams();
    for (const lpk of lpks) {
      params = params.append('lpks', lpk.toString());
    }
    return this._apiService.get(url);
  }

  getFormats(): Observable<Format[]> {
    const url = `${this._libraryApi}/formats`;
    return this._apiService.get(url) as Observable<Format[]>;
  }

  constructDefaultLibrary(): Library {
    return {
      lid: 'System Wide Default',
      libraryTypeId: 0,
      libraryGuid: '',
      hasEzproxy: false,
      ezproxyLinkText: '',
      ezproxyMsgText: '',
      ezproxyUri: '',
      hasSaml: false,
      samlSp: '',
      samlIdp: '',
      samlLinkText: '',
      samlMsgText: '',
      samlUrl: '',
      iphoneEnable: false,
      lat: 0,
      long: 0,
      patronLookupToolParticipant: false,
      lpk: 0,
      cid: this._userData.customerId,
      cpk: 0,
      name: '',
    };
  }

  libraryExistsValidator(libraries: any[]) {
    return (control: FormControl): ValidationErrors | null => {
      const exists = libraries.some((lib) => lib.lpk === control.value.lpk);

      return exists ? null : { invalidLibrary: true }; // Trigger error if not found
    };
  }

  libraryDisplayFn = (library: Library) => {
    if (library.lid && library.name) {
      return `${library.lid} : ${library.name}`;
    } else if (library.lid) {
      return library.lid;
    }

    return '';
  };
}
